/**
 * WP-V2-PK — ParkScene multiplayer (PLAN-V2 §6 Park).
 * AnimatedDogAvatar gracze/NPC; remote interp z ParkState V1 (zero nowych pól).
 * Plac zabaw animowany lokalnie (huśtawka/zjeżdżalnia/babki); piłka = sync NetClient.
 */
import Phaser from 'phaser';
import {
  EMOTE_IDS,
  type EmoteId,
  type ParkPlayerPublic,
  type ParkNpcPublic,
  DOG_SPEED,
} from '@pieskowo/shared';
import { BaseScene } from '../../core/BaseScene.js';
import { UiKit, MIN_TOUCH } from '../../core/UiKit.js';
import { getSceneRouter } from '../../core/SceneRouter.js';
import {
  AnimatedDogAvatar,
  preloadAnchors,
  normalizeOutfitSlots,
  emptyOutfitSlots,
  OutfitStore,
  type OutfitSlots,
} from '../../avatar/index.js';
import {
  ObjectLayer,
  playFx,
  type InteractEvent,
  type InteractTarget,
} from '../../interact/index.js';
import {
  setupWorld,
  follow,
  WalkController,
  type WalkableTarget,
} from '../../core/world/index.js';
import {
  NetClient,
  type ParkSnapshot,
  type NetPhase,
  applyServerCorrection,
  pushSample,
  interpolateAt,
  renderTimeNow,
  type PosSample,
} from '../../net/index.js';
import type { MovementState } from '@pieskowo/shared';
import { recordParkVisit } from './parkVisit.js';
import {
  INTERACT_BUS_EVENT,
  createParkKickProgress,
  recordParkKick,
  type ParkKickProgress,
} from './missionHook.js';
import {
  PARK_BG_ANCHORS,
  PARK_COLLIDERS,
  PARK_OBJECT_DEFS,
  PARK_PLACEMENTS,
  PARK_WORLD_H,
  PARK_WORLD_W,
  isParkBench,
  isPlaygroundRide,
  isSandbox,
} from './objects.js';
import {
  diffPlayers,
  diffNpcs,
  velocityBetween,
  velocityFromSamples,
} from './playerSync.js';
import {
  addSandcake,
  createSandcakeState,
  planLounge,
  planRide,
  rideKindFromDef,
  slidePose,
  swingPose,
  type SandcakeState,
  type RidePlan,
  type LoungePlan,
} from './playground.js';
import {
  ensurePlaygroundTextures,
  playgroundGroundPads,
} from './playgroundVisuals.js';

const EMOTE_LABELS_PL: Record<EmoteId, string> = {
  heart: '❤️',
  wave: '👋',
  happy: '😊',
  sleep: '😴',
  star: '⭐',
  ball: '⚽',
};

type RemoteVisual = {
  avatar: AnimatedDogAvatar;
  nameLabel: Phaser.GameObjects.Text;
  emoteLabel: Phaser.GameObjects.Text;
  samples: PosSample[];
  lastOutfit: string;
  dogName: string;
  lastX: number;
  lastY: number;
};

type NpcVisual = {
  avatar: AnimatedDogAvatar;
  emoteLabel: Phaser.GameObjects.Text;
  samples: PosSample[];
  hit: Phaser.GameObjects.Zone;
  lastX: number;
  lastY: number;
};

/** Thin WalkableTarget wrapping AnimatedDogAvatar for WalkController. */
class LocalPawn implements WalkableTarget {
  constructor(readonly avatar: AnimatedDogAvatar) {}
  get container(): Phaser.GameObjects.Container {
    return this.avatar.container;
  }
  moveTo(x: number, y: number): void {
    this.avatar.moveTo(x, y);
  }
  setVelocity(vx: number, vy: number): void {
    this.avatar.setVelocity(vx, vy);
  }
}

export class ParkScene extends BaseScene {
  static readonly KEY = 'Park';

  private net: NetClient | null = null;
  private localMove: MovementState = { x: 640, y: 400, tx: 640, ty: 400 };
  private localAvatar: AnimatedDogAvatar | null = null;
  private localPawn: LocalPawn | null = null;
  private walk: WalkController | null = null;
  private localName: Phaser.GameObjects.Text | null = null;
  private localEmote: Phaser.GameObjects.Text | null = null;
  private marker: Phaser.GameObjects.Arc | null = null;

  private remotes = new Map<string, RemoteVisual>();
  private npcs = new Map<string, NpcVisual>();
  private ballGfx: Phaser.GameObjects.Arc | null = null;
  private ballPos: { x: number; y: number } = {
    x: PARK_BG_ANCHORS.ball.x,
    y: PARK_BG_ANCHORS.ball.y,
  };

  private statusText: Phaser.GameObjects.Text | null = null;
  private sleepOverlay: Phaser.GameObjects.Container | null = null;

  private lastServerLocal: ParkPlayerPublic | null = null;
  private phase: NetPhase = 'idle';
  private heartFx: Phaser.GameObjects.GameObject[] = [];
  private moveThrottleAt = 0;
  private lastSentTx = 640;
  private lastSentTy = 400;

  private layer: ObjectLayer | null = null;
  private unsubLayer: (() => void) | null = null;
  private sandState: SandcakeState = createSandcakeState();
  /** W9-B: postęp misji park_ball (unikalne kickId). */
  private kickQuestProgress: ParkKickProgress = createParkKickProgress();
  private kickSeq = 0;
  private rideActive = false;
  private rideTween: Phaser.Tweens.Tween | null = null;
  private prevRemoteIds = new Set<string>();
  private prevNpcIds = new Set<string>();
  private useWalkNet = true;

  constructor() {
    super(ParkScene.KEY);
  }

  preload(): void {
    this.preloadRealBg('bg_park');
    for (const pose of ['idle', 'walkA', 'walkB', 'sit', 'jump', 'wave'] as const) {
      const key = `dog_maltese_${pose}`;
      if (!this.textures.exists(key)) {
        this.load.image(key, `/assets/real/maltese_${pose}.png`);
      }
    }
    if (!this.textures.exists('ph_dog')) {
      this.load.image('ph_dog', '/assets/placeholder/dog.png');
    }
    for (const key of [
      'obj_park_slide',
      'obj_park_swing',
      'obj_park_sandbox',
      'obj_park_sandcake',
    ] as const) {
      if (!this.textures.exists(key)) {
        this.load.image(key, `/assets/real/${key}.png`);
      }
    }
    void preloadAnchors(this);
  }

  create(): void {
    setupWorld(this, {
      worldW: PARK_WORLD_W,
      worldH: PARK_WORLD_H,
      bg: 0xa8e6cf,
    });
    this.addRealBg('bg_park');
    // PNG z preload; gdy brak — proceduralne kształty Toca (bez napisów)
    try {
      ensurePlaygroundTextures(this);
    } catch {
      /* headless */
    }
    this.drawDecor();
    this.spawnLocalPawn();
    this.spawnObjectLayer();
    // WAVE7-C: gdy tło ma już plac zabaw — ukryj dublujące „protezy" sprite'ów
    // (zostają tylko hit-zony w kotwicach bg_park).
    this.hideBgAlignedPlaygroundSprites();
    this.spawnBall();
    this.paintChrome();
    this.bindNet();

    void recordParkVisit();

    // Powrót z Wardrobe/Shop — odśwież strój lokalnego psa z OutfitStore.
    this.events.on('wake', this.refreshLocalOutfit, this);
    this.events.on('resume', this.refreshLocalOutfit, this);
    this.events.once('shutdown', () => this.teardown());
    this.events.once('destroy', () => this.teardown());
  }

  /** Strój z OutfitStore (nie martwy registry.get('dogOutfit')). */
  private refreshLocalOutfit(): void {
    if (!this.localAvatar) return;
    const cache = OutfitStore.load();
    const slots = cache.slots ?? emptyOutfitSlots();
    this.localAvatar.setOutfit(slots, cache.swimSlots);
    if (cache.breed || cache.color) {
      this.localAvatar.setBreed(cache.breed || 'maltese', cache.color);
    }
  }

  update(_t: number, dtMs: number): void {
    const dtSec = Math.min(0.05, dtMs / 1000);
    const now = Date.now();

    if (!this.rideActive) {
      // WalkController = input + kolizje; sync do predykcji net
      this.walk?.update(dtSec);
      if (this.walk && this.localAvatar) {
        const pos = this.walk.position;
        this.localMove = {
          x: pos.x,
          y: pos.y,
          tx: this.walk.getState().tx,
          ty: this.walk.getState().ty,
        };
        this.localAvatar.update(dtSec);
        this.localAvatar.container.setPosition(pos.x, pos.y);
        this.localAvatar.container.setDepth(100 + Math.floor(pos.y));
        this.syncNameEmoteToLocal();
        this.maybeSendMove();
      }

      // soft correction vs server
      if (this.lastServerLocal && this.walk) {
        const corrected = applyServerCorrection(this.localMove, {
          x: this.lastServerLocal.x,
          y: this.lastServerLocal.y,
          tx: this.lastServerLocal.tx,
          ty: this.lastServerLocal.ty,
        });
        if (
          corrected.x !== this.localMove.x ||
          corrected.y !== this.localMove.y
        ) {
          this.localMove = corrected;
          this.walk.setStateForTest({
            x: corrected.x,
            y: corrected.y,
            tx: corrected.tx,
            ty: corrected.ty,
          });
          this.localAvatar?.container.setPosition(corrected.x, corrected.y);
        } else {
          this.localMove = corrected;
        }
      }
    } else {
      this.localAvatar?.update(dtSec);
    }

    // remote + npc interp + walk anim
    const rt = renderTimeNow(now);
    for (const [, r] of this.remotes) {
      const p = interpolateAt(r.samples, rt);
      if (p) {
        const vel = velocityFromSamples(r.samples, rt);
        // fallback: delta klatki
        const frameVel =
          Math.hypot(vel.vx, vel.vy) < 4
            ? velocityBetween({ x: r.lastX, y: r.lastY }, p, dtSec)
            : vel;
        r.avatar.container.setPosition(p.x, p.y);
        r.avatar.container.setDepth(100 + Math.floor(p.y));
        r.avatar.setVelocity(frameVel.vx, frameVel.vy);
        r.avatar.update(dtSec);
        // pozycja autorytatywna z interpa (avatar.update mógłby przesunąć po velocity)
        r.avatar.container.setPosition(p.x, p.y);
        r.lastX = p.x;
        r.lastY = p.y;
        r.nameLabel.setPosition(p.x, p.y - 52);
        r.emoteLabel.setPosition(p.x, p.y - 78);
        r.nameLabel.setDepth(200 + Math.floor(p.y));
        r.emoteLabel.setDepth(201 + Math.floor(p.y));
      }
    }
    for (const [, n] of this.npcs) {
      const p = interpolateAt(n.samples, rt);
      if (p) {
        const vel = velocityFromSamples(n.samples, rt);
        const frameVel =
          Math.hypot(vel.vx, vel.vy) < 4
            ? velocityBetween({ x: n.lastX, y: n.lastY }, p, dtSec)
            : vel;
        n.avatar.container.setPosition(p.x, p.y);
        n.avatar.container.setDepth(90 + Math.floor(p.y));
        n.avatar.setVelocity(frameVel.vx, frameVel.vy);
        n.avatar.update(dtSec);
        n.avatar.container.setPosition(p.x, p.y);
        n.lastX = p.x;
        n.lastY = p.y;
        n.hit.setPosition(p.x, p.y);
        n.emoteLabel.setPosition(p.x, p.y - 48);
        n.emoteLabel.setDepth(95 + Math.floor(p.y));
      }
    }

    // piłka — lerp do snapshotu (sync istniejący)
    if (this.ballGfx) {
      this.ballGfx.x += (this.ballPos.x - this.ballGfx.x) * Math.min(1, dtSec * 12);
      this.ballGfx.y += (this.ballPos.y - this.ballGfx.y) * Math.min(1, dtSec * 12);
      this.ballGfx.setDepth(80 + Math.floor(this.ballGfx.y));
    }

    this.layer?.update(dtSec);

    // kotek companion — follow w świecie parku
    if (this.localAvatar) {
      this.registry.set('companionFollow', {
        x: this.localAvatar.x,
        y: this.localAvatar.y,
      });
    }
  }

  // ── world / UI ────────────────────────────────────────────────────────

  private drawDecor(): void {
    // bg_park już ma ścieżkę, plac i drzewa — nie dorysowujemy protez.
    // Soft-pady tylko gdy brak tła (fallback proceduralny).
    if (this.textures.exists('bg_park')) return;

    const w = PARK_WORLD_W;
    const h = PARK_WORLD_H;
    this.add
      .rectangle(w / 2, h * 0.55, w * 0.7, 48, 0xe8d5b7, 0.5)
      .setDepth(1);

    for (const pad of playgroundGroundPads()) {
      this.add
        .ellipse(pad.x, pad.y, pad.w, pad.h, pad.color, 0.45)
        .setDepth(2);
    }
  }

  /**
   * WAVE7-C: sprzęt jest namalowany na bg_park — dublujące PNG/proceduralne
   * sprite'y chowamy (alpha≈0), zostawiając interaktywne hit-zony.
   * Piaskownica i babki zostają widoczne (nie ma ich na tle).
   */
  private hideBgAlignedPlaygroundSprites(): void {
    if (!this.textures.exists('bg_park') || !this.layer) return;
    for (const obj of this.layer.getObjects()) {
      if (
        isPlaygroundRide(obj.def.id) ||
        isParkBench(obj.def.id)
      ) {
        obj.container.setAlpha(0.02);
      }
    }
  }

  private spawnLocalPawn(): void {
    const cache = OutfitStore.load();
    const breed =
      cache.breed || (this.registry.get('dogBreed') as string) || 'maltese';
    const color =
      cache.color || (this.registry.get('dogColor') as string) || undefined;
    const outfit = cache.slots ?? emptyOutfitSlots();

    this.localAvatar = new AnimatedDogAvatar({
      scene: this,
      x: this.localMove.x,
      y: this.localMove.y,
      breed,
      color,
      slots: outfit,
      scale: 0.9,
      depth: 200,
      walkSpeed: DOG_SPEED,
    });
    this.localPawn = new LocalPawn(this.localAvatar);

    this.localName = this.add
      .text(this.localMove.x, this.localMove.y - 52, 'Ty', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#4a3f55',
        fontStyle: 'bold',
        backgroundColor: '#ffffffcc',
        padding: { x: 6, y: 2 },
      })
      .setOrigin(0.5)
      .setDepth(300);

    this.localEmote = this.add
      .text(this.localMove.x, this.localMove.y - 78, '', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
      })
      .setOrigin(0.5)
      .setDepth(301);

    this.walk = new WalkController(this, this.localPawn, {
      colliders: PARK_COLLIDERS,
      bounds: {
        minX: NetClient.BOUNDS.minX,
        minY: NetClient.BOUNDS.minY,
        maxX: NetClient.BOUNDS.maxX,
        maxY: NetClient.BOUNDS.maxY,
      },
      speed: DOG_SPEED,
      enableInput: true,
    });

    follow(this, this.localAvatar.container, {
      lerp: 0.12,
      deadzone: { width: 120, height: 80 },
    });

    // Hook input: marker + net przy celach ruchu (bez nadpisywania metody TS)
    this.input.on('pointerdown', this.onWorldPointer, this);
  }

  private onWorldPointer(ptr: Phaser.Input.Pointer): void {
    if (this.rideActive) return;
    if (ptr.y > PARK_WORLD_H - 110) return;
    if (ptr.y < 80 && ptr.x < 200) return;
    // ball / NPC zone handle themselves via stopPropagation
    const wp = this.cameras.main.getWorldPoint(ptr.x, ptr.y);
    this.showMarker(wp.x, wp.y);
    // WalkController already moves on pointerdown; mirror target to net
    this.net?.sendMoveTo(wp.x, wp.y);
    this.lastSentTx = wp.x;
    this.lastSentTy = wp.y;
    this.moveThrottleAt = Date.now();
  }

  private syncNameEmoteToLocal(): void {
    if (!this.localAvatar) return;
    const x = this.localAvatar.x;
    const y = this.localAvatar.y;
    this.localName?.setPosition(x, y - 52).setDepth(300 + Math.floor(y));
    this.localEmote?.setPosition(x, y - 78).setDepth(301 + Math.floor(y));
  }

  private maybeSendMove(): void {
    if (!this.useWalkNet || !this.walk || this.rideActive) return;
    const st = this.walk.getState();
    if (st.mode === 'idle') return;
    const now = Date.now();
    if (now - this.moveThrottleAt < 50) return;
    if (
      Math.hypot(st.tx - this.lastSentTx, st.ty - this.lastSentTy) < 4 &&
      st.mode === 'path'
    ) {
      return;
    }
    this.moveThrottleAt = now;
    this.lastSentTx = st.tx;
    this.lastSentTy = st.ty;
    this.net?.sendMoveTo(st.tx, st.ty);
  }

  private spawnObjectLayer(): void {
    const self = this;
    const playerTarget: InteractTarget = {
      moveTo: (x, y) => {
        if (self.rideActive) return;
        self.walk?.moveTo(x, y);
      },
      holdItem: (tex) => {
        self.localAvatar?.holdItem(tex);
      },
      playAction: (a) => {
        if (!self.localAvatar) return Promise.resolve();
        if (a === 'sniff') return self.localAvatar.playAction('happy');
        const known = a as
          | 'sit'
          | 'jump'
          | 'wave'
          | 'eat'
          | 'swim'
          | 'sleep'
          | 'happy';
        return self.localAvatar.playAction(known);
      },
      getPosition: () =>
        self.walk?.position ?? {
          x: self.localAvatar?.x ?? 0,
          y: self.localAvatar?.y ?? 0,
        },
      get isMoving() {
        return self.walk?.isMoving ?? false;
      },
      get x() {
        return self.walk?.position.x ?? self.localAvatar?.x ?? 0;
      },
      get y() {
        return self.walk?.position.y ?? self.localAvatar?.y ?? 0;
      },
    };

    this.layer = new ObjectLayer({
      scene: this,
      defs: PARK_OBJECT_DEFS,
      target: playerTarget,
      interactRange: 110,
      groundBounds: {
        minX: NetClient.BOUNDS.minX,
        maxX: NetClient.BOUNDS.maxX,
        minY: 300,
        maxY: NetClient.BOUNDS.maxY,
      },
    });
    this.layer.spawn(PARK_PLACEMENTS);
    this.unsubLayer = this.layer.on((evt) => this.onInteract(evt));
  }

  private onInteract(evt: InteractEvent): void {
    this.events.emit('interact', evt);

    if (evt.type !== 'tap' && evt.type !== 'toggle') return;

    if (isPlaygroundRide(evt.defId)) {
      const kind = rideKindFromDef(evt.defId);
      if (!kind || this.rideActive) return;
      const plan = planRide(kind, evt.x, evt.y);
      void this.startLocalRide(plan);
      return;
    }

    if (isSandbox(evt.defId)) {
      this.makeSandcake();
      return;
    }

    if (isParkBench(evt.defId)) {
      if (this.rideActive) return;
      const plan = planLounge(evt.x, evt.y);
      void this.startLocalLounge(plan);
    }
  }

  /** Leżenie NA ławce (nie ikona obok) — Toca-feel. */
  private async startLocalLounge(plan: LoungePlan): Promise<void> {
    if (!this.localAvatar || this.rideActive) return;
    this.rideActive = true;
    this.localAvatar.stop();

    this.walk?.moveTo(plan.seat.x, plan.seat.y);
    this.localAvatar.moveTo(plan.seat.x, plan.seat.y);
    this.registry.set('parkRidePhase', 'approaching');
    this.setStatus('Podchodzę do ławki…');
    await this.waitUntilNear(plan.seat.x, plan.seat.y, 32, 2500);

    this.walk?.setStateForTest({
      x: plan.seat.x,
      y: plan.seat.y,
      tx: plan.seat.x,
      ty: plan.seat.y,
      mode: 'idle',
      vx: 0,
      vy: 0,
    });
    this.localAvatar.container.setPosition(plan.seat.x, plan.seat.y);
    this.localAvatar.setVelocity(0, 0);
    this.registry.set('parkRidePhase', 'sleeping');
    this.setStatus('Leżę na ławce… 😴');
    await this.localAvatar.playAction('sleep');
    await new Promise<void>((r) => this.time.delayedCall(plan.durationMs, () => r()));
    await this.localAvatar.playAction('happy');

    this.localMove = {
      x: plan.seat.x,
      y: plan.seat.y,
      tx: plan.seat.x,
      ty: plan.seat.y,
    };
    this.net?.sendMoveTo(plan.seat.x, plan.seat.y);
    playFx(this, 'hearts', plan.seat.x, plan.seat.y);
    this.localAvatar.container.setRotation(0);
    this.rideActive = false;
    this.registry.set('parkRidePhase', 'idle');
    this.setStatus('Park — tapnij, aby iść · plac zabaw · kopnij piłkę');
  }

  private makeSandcake(): void {
    const next = addSandcake(this.sandState);
    if (next.count === this.sandState.count) {
      this.setStatus('Piaskownica pełna babek! 🏖️');
      return;
    }
    this.sandState = next;
    const last = next.placements[next.placements.length - 1]!;
    this.layer?.spawn([last]);
    playFx(this, 'crumbs', last.x, last.y);
    void this.localAvatar?.playAction('happy');
    this.setStatus('Ulepiłam babkę z piasku! 🏝️');
  }

  private async startLocalRide(plan: RidePlan): Promise<void> {
    if (!this.localAvatar || this.rideActive) return;
    this.rideActive = true;
    this.localAvatar.stop();

    // podejdź do siedziska
    this.walk?.moveTo(plan.mount.x, plan.mount.y);
    this.localAvatar.moveTo(plan.mount.x, plan.mount.y);
    this.registry.set('parkRidePhase', 'approaching');
    this.setStatus('Podchodzę do atrakcji…');
    await this.waitUntilNear(plan.mount.x, plan.mount.y, 28, 2500);

    this.walk?.setStateForTest({
      x: plan.mount.x,
      y: plan.mount.y,
      tx: plan.mount.x,
      ty: plan.mount.y,
      mode: 'idle',
      vx: 0,
      vy: 0,
    });
    this.localAvatar.container.setPosition(plan.mount.x, plan.mount.y);
    this.localAvatar.setVelocity(0, 0);

    if (plan.kind === 'swing') {
      this.registry.set('parkRidePhase', 'swinging');
      this.setStatus('Huśtam się! 🎠');
      await this.playSwing(plan);
    } else {
      this.registry.set('parkRidePhase', 'sliding');
      this.setStatus('Zjeżdżam! 🛝');
      void this.localAvatar.playAction('jump');
      await this.playSlide(plan);
    }

    // zejście
    this.localAvatar.container.setPosition(plan.dismount.x, plan.dismount.y);
    this.localAvatar.container.setRotation(0);
    this.walk?.setStateForTest({
      x: plan.dismount.x,
      y: plan.dismount.y,
      tx: plan.dismount.x,
      ty: plan.dismount.y,
      mode: 'idle',
      vx: 0,
      vy: 0,
    });
    this.localMove = {
      x: plan.dismount.x,
      y: plan.dismount.y,
      tx: plan.dismount.x,
      ty: plan.dismount.y,
    };
    this.net?.sendMoveTo(plan.dismount.x, plan.dismount.y);
    playFx(this, 'sparkle', plan.dismount.x, plan.dismount.y);
    this.rideActive = false;
    this.registry.set('parkRidePhase', 'idle');
    this.setStatus('Park — tapnij, aby iść · plac zabaw · kopnij piłkę');
  }

  private playSwing(plan: RidePlan): Promise<void> {
    return new Promise((resolve) => {
      const base = { ...plan.mount };
      const proxy = { t: 0 };
      this.rideTween?.stop();
      this.rideTween = this.tweens.add({
        targets: proxy,
        t: 1,
        duration: plan.durationMs,
        ease: 'Sine.easeInOut',
        onUpdate: () => {
          if (!this.localAvatar) return;
          const p = swingPose(proxy.t, base);
          this.localAvatar.container.setPosition(p.x, p.y);
          this.localAvatar.container.setRotation(p.angle);
          this.syncNameEmoteToLocal();
        },
        onComplete: () => {
          this.localAvatar?.container.setRotation(0);
          this.rideTween = null;
          resolve();
        },
      });
    });
  }

  private playSlide(plan: RidePlan): Promise<void> {
    return new Promise((resolve) => {
      const proxy = { t: 0 };
      this.rideTween?.stop();
      this.rideTween = this.tweens.add({
        targets: proxy,
        t: 1,
        duration: plan.durationMs,
        ease: 'Quad.easeIn',
        onUpdate: () => {
          if (!this.localAvatar) return;
          const p = slidePose(proxy.t, plan.mount, plan.dismount);
          this.localAvatar.container.setPosition(p.x, p.y);
          this.syncNameEmoteToLocal();
        },
        onComplete: () => {
          this.rideTween = null;
          resolve();
        },
      });
    });
  }

  private waitUntilNear(
    x: number,
    y: number,
    radius: number,
    timeoutMs: number,
  ): Promise<void> {
    return new Promise((resolve) => {
      const start = Date.now();
      const tick = () => {
        // Podczas rideActive WalkController.update() jest pominięty (patrz
        // ParkScene.update), więc this.walk.position zamraża się na starej
        // wartości. Realny ruch pieska napędza AnimatedDogAvatar.update()
        // (gałąź else), więc odpytujemy jego faktyczną pozycję kontenera.
        const pos = this.localAvatar
          ? { x: this.localAvatar.x, y: this.localAvatar.y }
          : (this.walk?.position ?? { x: 0, y: 0 });
        if (Math.hypot(pos.x - x, pos.y - y) <= radius) {
          resolve();
          return;
        }
        if (Date.now() - start > timeoutMs) {
          // teleport soft
          this.walk?.setStateForTest({
            x,
            y,
            tx: x,
            ty: y,
            mode: 'idle',
            vx: 0,
            vy: 0,
          });
          this.localAvatar?.container.setPosition(x, y);
          resolve();
          return;
        }
        this.time.delayedCall(50, tick);
      };
      tick();
    });
  }

  private spawnBall(): void {
    this.ballGfx = this.add
      .circle(this.ballPos.x, this.ballPos.y, 18, 0xff6b6b)
      .setStrokeStyle(3, 0xffffff)
      .setDepth(80)
      .setInteractive({ useHandCursor: true });
    this.ballGfx.on('pointerdown', (ptr: Phaser.Input.Pointer) => {
      ptr.event?.stopPropagation?.();
      if (!this.localAvatar) return;
      const dx = this.ballGfx!.x - this.localAvatar.x;
      const dy = this.ballGfx!.y - this.localAvatar.y;
      const len = Math.hypot(dx, dy) || 1;
      this.net?.sendKickBall(dx / len, dy / len);
      this.setStatus('Piłka leci!');

      // W9-B: krok misji park_ball (unikalny kickId per lokalne kopnięcie)
      this.kickSeq += 1;
      const kickId = `local-${this.kickSeq}`;
      const result = recordParkKick(this.kickQuestProgress, kickId);
      this.kickQuestProgress = result.progress;
      if (result.payload) {
        this.game.events.emit(INTERACT_BUS_EVENT, result.payload);
        this.events.emit('quest-progress', result.payload);
      }
    });
  }

  private paintChrome(): void {
    this.add
      .text(PARK_WORLD_W / 2, 28, 'Park', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(400);

    this.statusText = this.add
      .text(PARK_WORLD_W / 2, PARK_WORLD_H - 18, 'Łączenie z parkiem…', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#4a3f55',
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(410);

    UiKit.button(this, {
      x: 100,
      y: 40,
      label: '← Miasto',
      width: 160,
      height: MIN_TOUCH,
      fill: 0xffd6e0,
      onClick: () => {
        void this.leaveAndGoTown();
      },
    });

    const emotes = EMOTE_IDS;
    const startX = PARK_WORLD_W / 2 - ((emotes.length - 1) * 72) / 2;
    emotes.forEach((id, i) => {
      const x = startX + i * 72;
      const y = PARK_WORLD_H - 72;
      const btn = this.add
        .circle(x, y, 28, 0xffffff, 0.92)
        .setStrokeStyle(3, 0xffb6c1)
        .setDepth(420)
        .setScrollFactor(0)
        .setInteractive({ useHandCursor: true });
      const label = this.add
        .text(x, y, EMOTE_LABELS_PL[id], { fontSize: '24px' })
        .setOrigin(0.5)
        .setDepth(421)
        .setScrollFactor(0);
      const fire = () => {
        this.net?.sendEmote(id);
        if (this.localEmote) {
          this.localEmote.setText(EMOTE_LABELS_PL[id]);
          this.time.delayedCall(2500, () => {
            if (this.localEmote?.text === EMOTE_LABELS_PL[id]) {
              this.localEmote.setText('');
            }
          });
        }
      };
      btn.on('pointerdown', (ptr: Phaser.Input.Pointer) => {
        ptr.event?.stopPropagation?.();
        fire();
      });
      if (56 < MIN_TOUCH) {
        const zone = this.add
          .zone(x, y, MIN_TOUCH, MIN_TOUCH)
          .setDepth(419)
          .setScrollFactor(0)
          .setInteractive();
        zone.on('pointerdown', (ptr: Phaser.Input.Pointer) => {
          ptr.event?.stopPropagation?.();
          fire();
        });
      }
      void label;
    });
  }

  private bindNet(): void {
    this.net = new NetClient({
      listener: {
        onPhase: (p, detail) => this.onPhase(p, detail),
        onSnapshot: (s) => this.onSnapshot(s),
        onNpcHearts: (id) => this.spawnHeartsAtNpc(id),
        onPetSmiled: () => {
          /* serduszka już z npcHearts */
        },
        onError: (code) => {
          this.setStatus(this.errorPl(code));
        },
      },
    });
    void this.net.connect();
  }

  private showMarker(x: number, y: number): void {
    this.marker?.destroy();
    this.marker = this.add
      .circle(x, y, 12, 0xffffff, 0.35)
      .setStrokeStyle(2, 0xffb6c1)
      .setDepth(5);
    this.tweens.add({
      targets: this.marker,
      scaleX: 1.5,
      scaleY: 1.5,
      alpha: 0,
      duration: 450,
      onComplete: () => {
        this.marker?.destroy();
        this.marker = null;
      },
    });
  }

  // ── net callbacks ─────────────────────────────────────────────────────

  private onPhase(phase: NetPhase, detail?: string): void {
    this.phase = phase;
    switch (phase) {
      case 'connected':
        this.hideSleepOverlay();
        this.setStatus(
          'Park — tapnij, aby iść · pogłaszcz pieska · plac zabaw · piłka',
        );
        break;
      case 'allocating':
      case 'joining':
        this.setStatus('Szukam placu zabaw…');
        break;
      case 'reconnecting':
        this.showSleepOverlay('Piesek śpi… łączę ponownie');
        this.setStatus(
          detail === 'session' ? 'Wznawiam zabawę…' : 'Chwileczkę, sieć wraca…',
        );
        break;
      case 'session_taken':
        this.hideSleepOverlay();
        this.setStatus('Zalogowano na innym urządzeniu');
        break;
      case 'offline':
        this.showSleepOverlay('Brak sieci — park czeka');
        this.setStatus('Jesteś offline. Lokalna zabawa bez przyjaciół.');
        break;
      case 'error':
        this.setStatus(this.errorPl(detail ?? 'ERROR'));
        break;
      case 'idle':
        this.hideSleepOverlay();
        break;
    }
  }

  private onSnapshot(snap: ParkSnapshot): void {
    const now = Date.now();
    const local = snap.players.find((p) => p.sessionId === snap.sessionId);
    if (local) {
      this.lastServerLocal = local;
      if (this.localName && local.dogName) {
        this.localName.setText(local.dogName);
      }
      if (
        this.localEmote &&
        local.emote &&
        EMOTE_LABELS_PL[local.emote as EmoteId]
      ) {
        this.localEmote.setText(EMOTE_LABELS_PL[local.emote as EmoteId]);
      }
      if (this.localAvatar && local.outfit) {
        try {
          const slots = normalizeOutfitSlots(local.outfit);
          this.localAvatar.setOutfit(slots);
          this.localAvatar.setBreed(local.breed, local.color);
        } catch {
          /* ignore */
        }
      }
      // pierwsze spawn — snap pozycji
      if (
        !this.rideActive &&
        Math.hypot(this.localMove.x - local.x, this.localMove.y - local.y) > 200
      ) {
        this.localMove = {
          x: local.x,
          y: local.y,
          tx: local.tx,
          ty: local.ty,
        };
        this.walk?.setStateForTest({
          x: local.x,
          y: local.y,
          tx: local.tx,
          ty: local.ty,
          mode: 'idle',
          vx: 0,
          vy: 0,
        });
        this.localAvatar?.container.setPosition(local.x, local.y);
      }
    }

    // remotes — onAdd / onChange / onRemove (diff)
    const pDiff = diffPlayers(this.prevRemoteIds, snap.players, snap.sessionId);
    for (const id of pDiff.removed) {
      this.destroyRemote(id);
    }
    for (const p of snap.players) {
      if (p.sessionId === snap.sessionId) continue;
      if (pDiff.added.includes(p.sessionId)) {
        this.createRemote(p, now);
      } else {
        this.updateRemote(p, now);
      }
    }
    this.prevRemoteIds = new Set(pDiff.remoteIds);

    // npcs
    const nDiff = diffNpcs(this.prevNpcIds, snap.npcs);
    for (const id of nDiff.removed) {
      this.destroyNpc(id);
    }
    for (const n of snap.npcs) {
      if (nDiff.added.includes(n.npcId)) {
        this.createNpc(n, now);
      } else {
        this.updateNpc(n, now);
      }
    }
    this.prevNpcIds = new Set(nDiff.remoteIds);

    this.ballPos = { x: snap.ball.x, y: snap.ball.y };
  }

  private createRemote(p: ParkPlayerPublic, now: number): void {
    if (this.remotes.has(p.sessionId)) {
      this.updateRemote(p, now);
      return;
    }
    let slots: OutfitSlots;
    try {
      slots = normalizeOutfitSlots(p.outfit);
    } catch {
      slots = emptyOutfitSlots();
    }
    const avatar = new AnimatedDogAvatar({
      scene: this,
      x: p.x,
      y: p.y,
      breed: p.breed || 'maltese',
      color: p.color,
      slots,
      scale: 0.85,
      depth: 100 + Math.floor(p.y),
      walkSpeed: DOG_SPEED,
    });
    const nameLabel = this.add
      .text(p.x, p.y - 52, p.dogName || 'Piesek', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '15px',
        color: '#4a3f55',
        fontStyle: 'bold',
        backgroundColor: '#ffffffbb',
        padding: { x: 5, y: 2 },
      })
      .setOrigin(0.5)
      .setDepth(200);
    const emoteLabel = this.add
      .text(p.x, p.y - 78, '', { fontSize: '26px' })
      .setOrigin(0.5)
      .setDepth(201);

    if (!p.connected) {
      avatar.container.setAlpha(0.55);
    }

    this.remotes.set(p.sessionId, {
      avatar,
      nameLabel,
      emoteLabel,
      samples: [{ t: now, x: p.x, y: p.y }],
      lastOutfit: p.outfit,
      dogName: p.dogName,
      lastX: p.x,
      lastY: p.y,
    });
  }

  private updateRemote(p: ParkPlayerPublic, now: number): void {
    const r = this.remotes.get(p.sessionId);
    if (!r) {
      this.createRemote(p, now);
      return;
    }
    r.samples = pushSample(r.samples, { t: now, x: p.x, y: p.y });
    if (r.dogName !== p.dogName) {
      r.dogName = p.dogName;
      r.nameLabel.setText(p.dogName || 'Piesek');
    }
    if (p.emote && EMOTE_LABELS_PL[p.emote as EmoteId]) {
      r.emoteLabel.setText(EMOTE_LABELS_PL[p.emote as EmoteId]);
    } else if (p.anim === 'zzz') {
      r.emoteLabel.setText('💤');
    } else {
      r.emoteLabel.setText('');
    }
    r.avatar.container.setAlpha(p.connected ? 1 : 0.55);
    if (p.outfit !== r.lastOutfit) {
      r.lastOutfit = p.outfit;
      try {
        r.avatar.setOutfit(normalizeOutfitSlots(p.outfit));
      } catch {
        /* ignore */
      }
    }
  }

  private destroyRemote(id: string): void {
    const r = this.remotes.get(id);
    if (!r) return;
    r.avatar.destroy();
    r.nameLabel.destroy();
    r.emoteLabel.destroy();
    this.remotes.delete(id);
  }

  private createNpc(n: ParkNpcPublic, now: number): void {
    if (this.npcs.has(n.npcId)) {
      this.updateNpc(n, now);
      return;
    }
    const avatar = new AnimatedDogAvatar({
      scene: this,
      x: n.x,
      y: n.y,
      breed: 'maltese',
      scale: 0.8,
      depth: 90 + Math.floor(n.y),
      walkSpeed: DOG_SPEED * 0.7,
    });
    const emoteLabel = this.add
      .text(n.x, n.y - 48, '', { fontSize: '22px' })
      .setOrigin(0.5)
      .setDepth(95);
    const tag = this.add
      .text(0, 40, 'Piesek', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '13px',
        color: '#4a3f55',
        backgroundColor: '#ffffff99',
        padding: { x: 4, y: 1 },
      })
      .setOrigin(0.5);
    avatar.container.add(tag);

    const hit = this.add
      .zone(n.x, n.y, MIN_TOUCH, MIN_TOUCH)
      .setDepth(95)
      .setInteractive({ useHandCursor: true });
    hit.on('pointerdown', (ptr: Phaser.Input.Pointer) => {
      ptr.event?.stopPropagation?.();
      this.net?.sendPetNpc(n.npcId);
      this.spawnHeartsAt(avatar.x, avatar.y);
      this.setStatus('Głaszczę pieska…');
      void avatar.playAction('happy');
    });

    this.npcs.set(n.npcId, {
      avatar,
      emoteLabel,
      samples: [{ t: now, x: n.x, y: n.y }],
      hit,
      lastX: n.x,
      lastY: n.y,
    });
  }

  private updateNpc(n: ParkNpcPublic, now: number): void {
    const v = this.npcs.get(n.npcId);
    if (!v) {
      this.createNpc(n, now);
      return;
    }
    v.samples = pushSample(v.samples, { t: now, x: n.x, y: n.y });
    if (n.emote === 'heart') {
      v.emoteLabel.setText('❤️');
    } else {
      v.emoteLabel.setText('');
    }
  }

  private destroyNpc(id: string): void {
    const v = this.npcs.get(id);
    if (!v) return;
    v.avatar.destroy();
    v.emoteLabel.destroy();
    v.hit.destroy();
    this.npcs.delete(id);
  }

  private spawnHeartsAtNpc(npcId: string): void {
    const v = this.npcs.get(npcId);
    if (!v) return;
    this.spawnHeartsAt(v.avatar.x, v.avatar.y);
  }

  private spawnHeartsAt(x: number, y: number): void {
    for (let i = 0; i < 5; i++) {
      const h = this.add
        .text(x + (Math.random() - 0.5) * 20, y - 20, '❤️', {
          fontSize: '22px',
        })
        .setDepth(500);
      this.heartFx.push(h);
      this.tweens.add({
        targets: h,
        y: y - 80 - Math.random() * 40,
        alpha: 0,
        duration: 800 + Math.random() * 400,
        onComplete: () => {
          h.destroy();
          this.heartFx = this.heartFx.filter((g) => g !== h);
        },
      });
    }
  }

  private showSleepOverlay(msg: string): void {
    this.hideSleepOverlay();
    const bg = this.add
      .rectangle(
        PARK_WORLD_W / 2,
        PARK_WORLD_H / 2,
        PARK_WORLD_W,
        PARK_WORLD_H,
        0x2d2a32,
        0.45,
      )
      .setScrollFactor(0)
      .setDepth(600);
    const bubble = this.add
      .rectangle(PARK_WORLD_W / 2, PARK_WORLD_H / 2, 420, 120, 0xffffff, 0.95)
      .setStrokeStyle(4, 0xffb6c1)
      .setScrollFactor(0)
      .setDepth(601);
    const txt = this.add
      .text(PARK_WORLD_W / 2, PARK_WORLD_H / 2, `💤  ${msg}`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '22px',
        color: '#4a3f55',
        fontStyle: 'bold',
        align: 'center',
        wordWrap: { width: 380 },
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(602);
    this.sleepOverlay = this.add
      .container(0, 0, [bg, bubble, txt])
      .setDepth(600);
  }

  private hideSleepOverlay(): void {
    this.sleepOverlay?.destroy(true);
    this.sleepOverlay = null;
  }

  private setStatus(msg: string): void {
    this.statusText?.setText(msg);
  }

  private errorPl(code: string): string {
    if (code === 'OFFLINE' || code === 'offline') return 'Brak internetu';
    if (code === 'JOIN_FAILED') return 'Nie udało się wejść do parku';
    if (code.startsWith('HTTP_401')) return 'Zaloguj się, aby bawić się w parku';
    return `Coś poszło nie tak (${code})`;
  }

  private async leaveAndGoTown(): Promise<void> {
    try {
      await this.net?.leave();
    } catch {
      /* ignore */
    }
    getSceneRouter().go('Town');
  }

  private teardown(): void {
    this.events.off('wake', this.refreshLocalOutfit, this);
    this.events.off('resume', this.refreshLocalOutfit, this);
    this.rideTween?.stop();
    this.rideTween = null;
    this.rideActive = false;
    this.input.off('pointerdown', this.onWorldPointer, this);
    this.unsubLayer?.();
    this.unsubLayer = null;
    this.layer?.destroy();
    this.layer = null;
    this.walk?.destroy();
    this.walk = null;
    void this.net?.leave();
    this.net?.destroy();
    this.net = null;
    for (const id of [...this.remotes.keys()]) this.destroyRemote(id);
    for (const id of [...this.npcs.keys()]) this.destroyNpc(id);
    this.localAvatar?.destroy();
    this.localAvatar = null;
    this.localPawn = null;
    this.localName?.destroy();
    this.localEmote?.destroy();
    this.marker?.destroy();
    this.ballGfx?.destroy();
    this.hideSleepOverlay();
    for (const h of this.heartFx) h.destroy();
    this.heartFx = [];
    this.prevRemoteIds.clear();
    this.prevNpcIds.clear();
    this.sandState = createSandcakeState();
    this.registry.remove('parkRidePhase');
    this.registry.remove('companionFollow');
  }
}

export default ParkScene;
